import { useContext, useMemo, useReducer, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import isEqual from "lodash-es/isEqual"
import merge from "lodash-es/merge"

import { ProductImage } from "scrivito/Objs/Product/Components/ProductImage"
import { useAPIHealth } from "shared/api/health/useAPIHealth"
import { last } from "shared/lib/arrayUtils"
import { useEvent } from "shared/lib/useEvent"
import {
  ExternalDimensioningConfigurationMismatchError,
  VARIANT_KEY_MISMATCH_ERROR
} from "shared/types/ExternalDimensioning"
import { ProductConfigurationFormData } from "shared/types/ProductConfiguration"
import { ProductConfiguratorMode } from "shared/types/ProductConfiguratorMode"
import {
  ExternalDimensioningResult,
  ProductConfigurationResponse
} from "shared/types/__generated__/graphql"
import { RJSFFormContext } from "shared/ui/rjsf-form/RJSFFormContext"

import { useExternalDimensioningConfigurationLazyQuery } from "entities/dim-configuration/graphql/external-dimensioning.queries.generated"
import { getDefaultFormData } from "entities/dim-configuration/lib/getDefaultFormData"
import { isValidVariantKey } from "entities/products/lib/productUtils"
import { useProductConfigurationAPI } from "entities/products/model/useProductConfigurationAPI"
import { useProductConfigurationSchemaProcessor } from "entities/products/model/useProductConfigurationSchemaProcessor"
import { useProductInformation } from "entities/products/model/useProductInformation"
import { useProductObjLoad } from "entities/products/model/useProductObjLoad"
import { useVariantKey } from "entities/products/model/useVariantKey"

import {
  ProductConfiguratorAPI,
  ProductConfiguratorModel
} from "../ProductConfigurator"
import { AttributeInformationContextType } from "./AttributeInformationContext"
import { getExternalDimensioningMismatches } from "./getExternalDimensioningMismatches"
import { useExternalProductDimensioningModel } from "./useExternalProductDimensioningModel"
import { useProductConfiguratorAddons } from "./useProductConfiguratorAddons"

export interface ProductConfiguratorResult {
  variantKey: string
  configurationKey?: string | null
  externalDimensioningResult?: ExternalDimensioningResult | null
}

interface UseProductConfiguratorModelProps {
  variantKey: string
  configurationKey?: string | null | undefined
  externalDimensioningResult?: ExternalDimensioningResult | null
  mode?: ProductConfiguratorMode
  onSubmit?: (configuration: ProductConfiguratorResult) => void
  isSubmitting?: boolean
}

function configurationReducer(
  state: ProductConfiguratorResult,
  action: Partial<ProductConfiguratorResult>
): ProductConfiguratorResult {
  return merge({}, state, action)
}

export function useProductConfiguratorModel({
  onSubmit,
  isSubmitting,
  ...rest
}: UseProductConfiguratorModelProps): ProductConfiguratorModel {
  const { t } = useTranslation()
  const { resetAll } = useContext(RJSFFormContext)
  const { productType } = useVariantKey(rest.variantKey)
  const productConfiguratorAPIRef: React.MutableRefObject<ProductConfiguratorAPI | null> =
    useRef<ProductConfiguratorAPI>(null)

  const [state, dispatch] = useReducer(configurationReducer, rest)

  const {
    productObj,
    expertAttributes,
    hiddenAttributes,
    isConfigurationWip,
    titleShort,
    highestMatchingProductBaseVariantObj,
    kind
  } = useProductObjLoad(state.variantKey)

  const attributeInformationContext = useMemo<AttributeInformationContextType>(
    () => ({
      productObj: productObj || null
    }),
    [productObj]
  )

  const handleVariantKeyChange = useEvent((nextVariantKey: string) => {
    onSubmit?.({ variantKey: nextVariantKey })
  })

  const { productConfiguration: isConfigurationServiceAvailable } =
    useAPIHealth()

  const processSchema = useProductConfigurationSchemaProcessor({
    expertAttributes,
    hiddenAttributes
  })

  const [formData, setFormData] =
    useState<ProductConfigurationFormData | null>()
  const [selectedDimensioningResultID, setSelectedDimensioningResultID] =
    useState<string | null>(null)
  const [lastChangedFieldID, setLastChangedFieldID] = useState<string | null>(
    null
  )

  const defaultFormDataRef = useRef(formData || undefined)

  const handleQueryCompleted = useEvent(
    (response: ProductConfigurationResponse) => {
      if (response.schema) {
        const nextFormData = getDefaultFormData(
          processSchema({
            schema: response.schema,
            filterHidden: false,
            filterSingleChoice: false,
            filterHiddenAttributes: true
          })
        )

        resetAll(nextFormData || undefined)
        setFormData(nextFormData)
        defaultFormDataRef.current = nextFormData || undefined
        setSelectedDimensioningResultID(
          response.schema.properties?.dimensioning?.id || null
        )
      }
    }
  )

  const handleError = useEvent(() => {
    resetAll(defaultFormDataRef.current)
  })

  const {
    isConfigurable,
    isConfigurationModelFaulty,
    productConfigurationResponse,
    isLoading: isLoadingSchema,
    isLoadingModels,
    fetchByConfigurationKey,
    fetchByFormData
  } = useProductConfigurationAPI({
    variantKey: state.variantKey,
    configurationKey: state.configurationKey ?? "",
    externalDimensioningID:
      state.externalDimensioningResult?.transactionId ?? "",
    onCompleted: handleQueryCompleted,
    onError: handleError,
    onConfigurationChange: dispatch,
    processSchema
  })

  const [
    fetchExternalDimensioningConfiguration,
    { loading: isLoadingExternalDimensioningConfiguration }
  ] = useExternalDimensioningConfigurationLazyQuery()

  const handleExternalDimensioningChange = useEvent(
    (externalDimensioningResult: ExternalDimensioningResult) => {
      if (!isConfigurable) {
        fetchExternalDimensioningConfiguration({
          variables: {
            dimensioningKey: externalDimensioningResult.transactionId,
            productType
          }
        }).then(({ data }) => {
          dispatch({
            externalDimensioningResult,
            variantKey:
              data?.externalDimensioningConfiguration?.variantKey ??
              productType,
            configurationKey: null
          })
        })
      } else {
        dispatch({ externalDimensioningResult, configurationKey: null })
      }
      productConfiguratorAPIRef.current?.onCompleteExternalProductDimensioning(
        externalDimensioningResult
      )
    }
  )

  const externalProductDimensioningModel = useExternalProductDimensioningModel({
    productType,
    defaultExternalDimensioningResult: rest.externalDimensioningResult,
    onExternalDimensioningChange: handleExternalDimensioningChange
  })

  const handleDimensioningSelect = useEvent((dimensioningID: string) => {
    const selectedResult =
      productConfigurationResponse?.dimensioningOptions?.find(
        option => option.dimensioningKey === dimensioningID
      )
    if (selectedResult && selectedResult.configurationKey) {
      setSelectedDimensioningResultID(dimensioningID)
      fetchByConfigurationKey(selectedResult.configurationKey)
      onSubmit?.({
        variantKey: state.variantKey,
        configurationKey: selectedResult.configurationKey
      })
    }
  })

  const processedSchema = useMemo(
    () =>
      productConfigurationResponse?.schema
        ? processSchema({
            schema: productConfigurationResponse.schema,
            filterHiddenAttributes: true
          })
        : undefined,
    [productConfigurationResponse?.schema, processSchema]
  )

  const handleFormDataChange = useEvent(
    (nextFormData: ProductConfigurationFormData, path?: string) => {
      if (path && !isEqual(formData, nextFormData)) {
        const fieldID = last(path.split("."))
        setFormData(nextFormData)
        setLastChangedFieldID(fieldID || null)
        fetchByFormData(nextFormData, fieldID)
      }
    }
  )

  const isConfigurationValid =
    isValidVariantKey(productConfigurationResponse?.variantKey) &&
    !productConfigurationResponse?.error

  const isLoadingProductConfigurationSchema =
    isConfigurable === false ? false : !processedSchema && isLoadingSchema

  const isUpdatingProductConfigurationSchema = isConfigurable && isLoadingSchema

  const { addonOptions, deleteAddon } = useProductConfiguratorAddons({
    productType,
    schema: processedSchema,
    addonVariantKeys: productConfigurationResponse?.accessories,
    formData,
    lastChangedFieldID,
    onFormDataChange: handleFormDataChange,
    isLoading: isLoadingSchema,
    productObj
  })

  const externalDimensioningConfigurationMismatchError = useMemo<
    ExternalDimensioningConfigurationMismatchError | null | undefined
  >(() => {
    if (!productConfigurationResponse) {
      return productConfigurationResponse
    }
    if (productConfigurationResponse.errors) {
      const mismatchError = productConfigurationResponse.errors.find(
        error => error.code === VARIANT_KEY_MISMATCH_ERROR
      )
      if (mismatchError)
        return mismatchError as ExternalDimensioningConfigurationMismatchError
    }
    return null
  }, [productConfigurationResponse])

  const hasMismatches =
    getExternalDimensioningMismatches(
      externalDimensioningConfigurationMismatchError
    ).length > 0

  const errorMessages = useMemo(() => {
    if (
      isLoadingProductConfigurationSchema ||
      isUpdatingProductConfigurationSchema
    ) {
      return null
    }
    const result = []

    if (productConfigurationResponse?.variantKey?.startsWith("SONDER")) {
      result.push(
        `${t("Product.Configuration.InvalidVariantKey")}: ${
          productConfigurationResponse?.variantKey
        }`
      )
    }

    if (productConfigurationResponse?.error) {
      result.push(productConfigurationResponse?.error)
    }
    if (productConfigurationResponse?.errors) {
      result.push(
        ...productConfigurationResponse.errors
          .filter(e => e.code !== VARIANT_KEY_MISMATCH_ERROR || !hasMismatches)
          .map(e => e.message || e.code)
      )
    }
    return result.length > 0 ? result : null
  }, [
    productConfigurationResponse,
    isLoadingProductConfigurationSchema,
    isUpdatingProductConfigurationSchema,
    hasMismatches
  ])

  const handleSubmit = useEvent(() => {
    onSubmit?.({
      variantKey: state.variantKey,
      configurationKey: state.configurationKey || null,
      externalDimensioningResult: state.externalDimensioningResult || null
    })
  })

  const { productInformation, isLoading: isLoadingProductInformation } =
    useProductInformation({
      variantKey: state.variantKey,
      configurationKey: state.configurationKey,
      dimensioningKey: state.externalDimensioningResult?.transactionId
    })

  return {
    schema: processedSchema,
    formData,

    mode: rest.mode || ProductConfiguratorMode.CONFIGURATION,

    onFormDataChange: handleFormDataChange,

    productConfigurationResponse:
      productConfigurationResponse?.productType === productType
        ? productConfigurationResponse
        : null,

    addonOptions,
    onAddonDelete: deleteAddon,

    selectedDimensioningID: selectedDimensioningResultID,
    dimensioningResults: productConfigurationResponse?.dimensioningOptions,
    onDimensioningSelect: handleDimensioningSelect,

    isReady:
      !externalProductDimensioningModel.isLoadingExternalDimensioningModels &&
      !isLoadingModels,

    isLoading: isLoadingProductConfigurationSchema,
    isUpdating: isUpdatingProductConfigurationSchema,
    isDisabled: isUpdatingProductConfigurationSchema,
    isSubmitting,

    hasError: isConfigurationValid,
    errorMessages,

    isConfigurable,
    isConfigurationModelFaulty,
    isConfigurationServiceAvailable,
    isConfigurationWip,

    variantKey: state.variantKey,
    onVariantKeyChange: handleVariantKeyChange,

    externalProductDimensioningModel,
    externalDimensioningConfigurationMismatchError,

    onSubmit: handleSubmit,

    productTitle: titleShort,
    productKind: kind,
    productImage: (
      <ProductImage obj={highestMatchingProductBaseVariantObj || productObj} />
    ),

    productInformation,
    isLoadingProductInformation:
      isLoadingProductInformation || isLoadingExternalDimensioningConfiguration,

    apiRef: productConfiguratorAPIRef,
    attributeInformationContext
  }
}
